<!--
  Interart - pagina admin "Etiqueta automatica" (dono do lead).
  Destino no fonte: /opt/chatwoot-src/app/javascript/dashboard/routes/dashboard/settings/autotag/Index.vue
  Espelha labels/Index.vue (SettingsLayout + BaseSettingsHeader, <script setup>, useStoreGetters/useStore, useI18n, useAlert).
  Pickers usam os stores existentes: labels/getLabels (+ labels/get) e agents/getVerifiedAgents (+ agents/get).
  Load/save via axios importado (mesma instancia default que o app configura com os headers de auth).
-->
<script setup>
import axios from 'axios';
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const AUTOTAG_ENDPOINT = '/api/v2/interart/autotag_config';

// Auth: o axios default deste build NAO carrega os headers do agente, entao montamos
// eles a partir do cookie cw_d_session_info (mesmo metodo comprovado do interart-labels.js).
function chatwootAuthHeaders() {
  try {
    const m = document.cookie.match(/cw_d_session_info=([^;]+)/);
    if (!m) return {};
    const parsed = JSON.parse(decodeURIComponent(m[1]));
    const h = {};
    ['access-token', 'client', 'uid', 'expiry', 'token-type'].forEach(k => {
      if (parsed[k]) h[k] = parsed[k];
    });
    return h;
  } catch (e) {
    return {};
  }
}

const authConfig = () => ({ headers: chatwootAuthHeaders(), withCredentials: true });

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const isLoading = ref(true);
const isSaving = ref(false);
const enabled = ref(false);
// Cada linha: { label: '<title>', email: '<email do atendente>' }
const mappings = ref([]);

// --- Pickers vindos dos stores existentes (sem chamada de API na mao) ---
const labelRecords = computed(() => getters['labels/getLabels'].value);
const agentRecords = computed(() => getters['agents/getVerifiedAgents'].value);

const labelOptions = computed(() =>
  labelRecords.value.map(label => ({
    value: label.title,
    label: label.title,
  }))
);

const agentOptions = computed(() =>
  agentRecords.value.map(agent => ({
    value: agent.email,
    label: agent.name ? `${agent.name} (${agent.email})` : agent.email,
  }))
);

const addRow = () => {
  mappings.value.push({ label: '', email: '' });
};

const removeRow = index => {
  mappings.value.splice(index, 1);
};

const loadConfig = async () => {
  isLoading.value = true;
  try {
    const { data } = await axios.get(AUTOTAG_ENDPOINT, authConfig());
    enabled.value = Boolean(data?.enabled);
    mappings.value = Array.isArray(data?.mappings)
      ? data.mappings.map(m => ({ label: m.label ?? '', email: m.email ?? '' }))
      : [];
  } catch (error) {
    useAlert(t('AUTOTAG.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const saveConfig = async () => {
  isSaving.value = true;
  try {
    const payload = {
      enabled: enabled.value,
      mappings: mappings.value
        .filter(m => m.label && m.email)
        .map(m => ({ label: m.label, email: m.email })),
    };
    const { data } = await axios.put(AUTOTAG_ENDPOINT, payload, authConfig());
    enabled.value = Boolean(data?.enabled);
    mappings.value = Array.isArray(data?.mappings)
      ? data.mappings.map(m => ({ label: m.label ?? '', email: m.email ?? '' }))
      : payload.mappings;
    useAlert(t('AUTOTAG.API.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('AUTOTAG.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onBeforeMount(() => {
  store.dispatch('labels/get');
  store.dispatch('agents/get');
  loadConfig();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('AUTOTAG.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AUTOTAG.HEADER')"
        :description="$t('AUTOTAG.DESCRIPTION')"
      >
        <template #actions>
          <Button
            :label="$t('AUTOTAG.SAVE')"
            size="sm"
            :is-loading="isSaving"
            @click="saveConfig"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col w-full gap-6">
        <!-- Liga/desliga -->
        <div
          class="flex items-center justify-between w-full px-4 py-3 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
        >
          <div class="flex flex-col gap-1">
            <span class="text-heading-3 text-n-slate-12">
              {{ $t('AUTOTAG.TOGGLE.LABEL') }}
            </span>
            <span class="text-body-main text-n-slate-11">
              {{ $t('AUTOTAG.TOGGLE.DESCRIPTION') }}
            </span>
          </div>
          <Switch v-model="enabled" />
        </div>

        <!-- Mapeamentos -->
        <div class="flex flex-col w-full gap-3">
          <div class="flex items-center justify-between w-full">
            <span class="text-heading-3 text-n-slate-12">
              {{ $t('AUTOTAG.MAPPINGS.TITLE') }}
            </span>
            <Button
              :label="$t('AUTOTAG.MAPPINGS.ADD')"
              icon="i-lucide-plus"
              size="sm"
              variant="outline"
              color="slate"
              @click="addRow"
            />
          </div>

          <p
            v-if="!mappings.length"
            class="py-10 text-n-slate-11 flex items-center justify-center text-body-main"
          >
            {{ $t('AUTOTAG.MAPPINGS.EMPTY') }}
          </p>

          <div
            v-for="(row, index) in mappings"
            v-else
            :key="index"
            class="flex items-end gap-3 w-full"
          >
            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <label class="text-sm text-n-slate-11">
                {{ $t('AUTOTAG.MAPPINGS.LABEL_COLUMN') }}
              </label>
              <ComboBox
                v-model="row.label"
                :options="labelOptions"
                :placeholder="$t('AUTOTAG.MAPPINGS.LABEL_PLACEHOLDER')"
                :search-placeholder="$t('AUTOTAG.MAPPINGS.LABEL_SEARCH')"
                :empty-state="$t('AUTOTAG.MAPPINGS.LABEL_EMPTY')"
              />
            </div>

            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <label class="text-sm text-n-slate-11">
                {{ $t('AUTOTAG.MAPPINGS.AGENT_COLUMN') }}
              </label>
              <ComboBox
                v-model="row.email"
                :options="agentOptions"
                :placeholder="$t('AUTOTAG.MAPPINGS.AGENT_PLACEHOLDER')"
                :search-placeholder="$t('AUTOTAG.MAPPINGS.AGENT_SEARCH')"
                :empty-state="$t('AUTOTAG.MAPPINGS.AGENT_EMPTY')"
              />
            </div>

            <Button
              v-tooltip.top="$t('AUTOTAG.MAPPINGS.REMOVE')"
              icon="i-lucide-trash-2"
              size="sm"
              color="slate"
              variant="ghost"
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2 mb-0.5"
              @click="removeRow(index)"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
